/**
 * useDialogBackBinding — store-based modal popup 을 activityStore.dialogs 와 양방향 sync.
 *
 * Source: docs/12_NAVIGATION_ACTIVITY_MODEL.md decision D4 (blocking dialog priority).
 *
 * 사용처: AddressFormPopup, AddressChangeSheet, ExitConfirmDialog 처럼 자체 zustand
 * state 로 visibility 를 관리하는 풀스크린 팝업/시트가 hardware back / browser back
 * 으로도 닫히도록 만든다.
 *
 *   useDialogBackBinding({
 *     isOpen: mode !== "closed",
 *     close: () => useAddressForm.getState().close(),
 *     kind: "address-form",
 *   });
 *
 * 동작 경로:
 *   (a) hardware back (Capacitor):
 *       useHardwareBackHandler → resolveBack → close-dialog(top.id) → store.closeDialog.
 *       본 훅의 zustand subscribe 가 우리 id 가 stack 에서 사라진 걸 감지 → popup close().
 *
 *   (b) browser back (popstate, iOS swipe-back 포함):
 *       open 시점에 같은 URL 로 sentinel history entry 를 push (RR navigate). 브라우저
 *       back 은 sentinel 을 먼저 소비하므로 페이지 URL 은 안 바뀌고 RR location.state
 *       에서 sentinel 만 사라진다. useNavigationType="POP" + sentinel 부재 감지 → close.
 *
 *   (c) programmatic close (X 버튼/저장/외부 store 변경):
 *       isOpen → false 로 바뀌면 effect cleanup 이 dialog stack 항목과 sentinel history
 *       를 모두 정리한다.
 *
 *   (d) dialog stacking — 시트 위에 폼이 또 떴을 때 (AddressChangeSheet 위 AddressFormPopup
 *       등): 각자 자기 id 를 stack 에 push 하고 history sentinel 도 각자 push. back 한 번
 *       = 위 dialog 만 close (아래 dialog 는 그대로 유지). subscribe 는 우리 id 가
 *       stack 에서 빠진 순간만 fire 하므로 다른 id 가 push 된다고 false-close 되지 않는다.
 */
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useActivityStore } from "../store/activityStore";

interface DialogBackBindingArgs {
  /** 팝업의 visible 상태. true 일 때 dialog 등록 + sentinel push. */
  isOpen: boolean;
  /** back-press 으로 dialog 가 닫힐 때 호출될 popup-store close 함수. */
  close: () => void;
  /** 디버그/추적용 kind. e.g. "address-form". */
  kind: string;
  /**
   * true 면 back-press 를 "흡수"한다 — dialog 가 stack 에서 pop 돼도 close() 를
   * 호출하지 않고 즉시 재점유(re-arm)해 underlying 네비게이션으로 새지 않게 한다.
   * OTA 적용 중("업데이트 중" 오버레이)·필수 업데이트 게이트처럼 사용자가 뒤로가기로
   * 빠져나가면 안 되는 blocking 상태에서 사용. 동적으로 바뀌어도 remount 없이 반영(ref).
   * 미지정(false) 이면 기존 동작 — back = close.
   */
  blockClose?: boolean;
}

const SENTINEL_FIELD = "__dialogSentinel";

interface SentinelState {
  [SENTINEL_FIELD]?: string;
}

function readSentinelKey(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const v = (state as SentinelState)[SENTINEL_FIELD];
  return typeof v === "string" ? v : null;
}

/** RR v6 BrowserHistory 는 window.history.state.usr 에 user state 를 둔다. */
function readHistoryUserState(): unknown {
  const s = window.history.state;
  if (typeof s !== "object" || s === null) return null;
  if ("usr" in s) return (s as { usr: unknown }).usr;
  return s;
}

export function useDialogBackBinding({
  isOpen,
  close,
  kind,
  blockClose = false,
}: DialogBackBindingArgs): void {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  // 점유 중인 dialog id / sentinel key — 없으면 null.
  const ownedDialogIdRef = useRef<string | null>(null);
  const sentinelKeyRef = useRef<string | null>(null);
  // blockClose 최신값 — closure stale 방지 (prompt→applying 전환 시 remount 없이 반영).
  const blockCloseRef = useRef(blockClose);
  blockCloseRef.current = blockClose;
  // sentinel 이 location.state 에 한 번이라도 land 한 적이 있는지. 직전 close 의
  // navigate(-1) 가 만든 navigationType="POP" 잔상이 새 open 의 첫 렌더에서
  // "sentinel popped" 으로 오인되어 즉시 닫히는 race 를 막는다.
  const sentinelLandedRef = useRef(false);

  // 최신 close / location — closure stale 방지.
  const closeRef = useRef(close);
  closeRef.current = close;
  const locationRef = useRef(location);
  locationRef.current = location;

  // History sentinel push — browser back 경로. 같은 URL 로 entry 1개 push.
  // 최초 open + blockClose re-arm(hardware/browser 양쪽) 에서 재사용.
  // URL 은 window.location 이 아니라 RR location 으로 만든다 — MemoryRouter
  // (테스트)에서는 window.location 이 라우터와 동기화되지 않아 "/" 로 navigate
  // 되며 페이지가 통째로 언마운트된다. BrowserRouter 에서는 두 값이 같다.
  const pushSentinel = useCallback(() => {
    const sentinelKey = `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sentinelKeyRef.current = sentinelKey;
    const loc = locationRef.current;
    const url = loc.pathname + loc.search + loc.hash;
    const baseState =
      typeof loc.state === "object" && loc.state !== null
        ? (loc.state as Record<string, unknown>)
        : {};
    navigate(url, {
      state: { ...baseState, [SENTINEL_FIELD]: sentinelKey },
    });
  }, [navigate, kind]);

  useEffect(() => {
    if (!isOpen) return;

    // (a) Dialog stack push — hardware back 경로.
    const id = useActivityStore.getState().openDialog(kind);
    ownedDialogIdRef.current = id;

    // (b) History sentinel.
    pushSentinel();

    // (c) Subscribe — 우리 id 가 dialog stack 에서 빠진 순간 처리.
    //     stack 모델이라 위에 다른 dialog 가 push 돼도 우리 id 는 그대로라 fire 안함.
    //     blockClose=true → close 대신 즉시 재점유(re-arm)해 back 을 흡수.
    const unsubscribe = useActivityStore.subscribe((state, prev) => {
      const ourId = ownedDialogIdRef.current;
      if (ourId === null) return;
      const wasPresent = prev.dialogs.some((d) => d.id === ourId);
      const stillPresent = state.dialogs.some((d) => d.id === ourId);
      if (!wasPresent || stillPresent) return;
      if (blockCloseRef.current) {
        // 뒤로가기 흡수 — dialog 재점유 + sentinel 재push. close 호출 안 함.
        // (재점유마다 history entry 가 1개 늘지만 적용/게이트는 짧고 reload 로 정리됨.)
        ownedDialogIdRef.current = useActivityStore.getState().openDialog(kind);
        pushSentinel();
      } else {
        ownedDialogIdRef.current = null;
        closeRef.current();
      }
    });

    return () => {
      unsubscribe();
      // dialog stack 정리 — 우리 id 가 아직 있으면 제거.
      const ourId = ownedDialogIdRef.current;
      if (ourId !== null) {
        useActivityStore.getState().closeDialog(ourId);
      }
      ownedDialogIdRef.current = null;
      // Sentinel 정리 — 아직 우리가 top 이면 pop. 이미 browser back 으로 pop 됐으면 skip.
      if (
        sentinelKeyRef.current !== null &&
        readSentinelKey(readHistoryUserState()) === sentinelKeyRef.current
      ) {
        sentinelKeyRef.current = null;
        navigate(-1);
      } else {
        sentinelKeyRef.current = null;
      }
    };
    // navigate / location 은 effect 내부에서 ref/직접 호출로만 쓰기 때문에 deps 에서
    // 의도적으로 제외. isOpen / kind 만 lifecycle trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, kind]);

  // Browser back 으로 sentinel 이 popped — close popup.
  // sentinel 이 한 번이라도 land 한 뒤 사라진 케이스만 진짜 POP 으로 인정한다.
  // (push 직후 첫 렌더는 아직 navigate 가 location 에 반영되기 전이므로 stale POP
  //  잔상으로 잘못 trigger 되는 걸 방지.)
  useEffect(() => {
    if (!isOpen) {
      sentinelLandedRef.current = false;
      return;
    }
    const ourKey = sentinelKeyRef.current;
    if (ourKey === null) {
      sentinelLandedRef.current = false;
      return;
    }
    const stateKey = readSentinelKey(location.state);
    if (stateKey === ourKey) {
      sentinelLandedRef.current = true;
      return;
    }
    if (!sentinelLandedRef.current) return;
    if (navigationType !== "POP") return;
    sentinelLandedRef.current = false;
    if (blockCloseRef.current) {
      // 브라우저 백(iOS 스와이프 등) 흡수 — sentinel 재push. dialog stack 은 그대로 점유.
      pushSentinel();
      return;
    }
    sentinelKeyRef.current = null;
    closeRef.current();
  }, [navigationType, location.key, location.state, isOpen, pushSentinel]);
}
